import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import styles from "./ProgramNav.module.css";
import { useRouter } from "next/router";

interface ProgramNavProps {
  className?: string;
}

const ProgramNav: React.FC<ProgramNavProps> = ({ className }) => {
  const [activeSection, setActiveSection] = useState<string>("overview");
  const [isSticky, setIsSticky] = useState(false);
  const router = useRouter();
  const { slug } = router.query;
  const navRefs = useRef<{ [key: string]: HTMLLIElement | null }>({});

  useEffect(() => {
    setActiveSection("overview");
  }, [slug]);

  const sections = useMemo(
    () => [
      { id: "overview", label: "Overview" },
      { id: "core-modules", label: "Core Modules" },
      { id: "admission-process", label: "Admission Process" },
      { id: "financial-aid", label: "Financial Aid" },
      { id: "testimonials", label: "Testimonials" },
    ],
    []
  );
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkIfMobile();

    window.addEventListener("resize", checkIfMobile);

    return () => {
      window.removeEventListener("resize", checkIfMobile);
    };
  }, []);

  const scrollToSection = (id: string) => {
    const section = document.getElementById(id);
    const isLpPage = router.pathname.includes("/lp");
    const offset = isLpPage ? (isMobile ? 70 : 100) : 180;

    if (section) {
      const topPosition =
        section.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top: topPosition, behavior: "smooth" });
    }
  };

  const handleScroll = useCallback(() => {
    // Check if nav is sticky
    const nav = document.querySelector(`.${styles.stickyNav}`);
    if (nav) {
      const rect = nav.getBoundingClientRect();
      setIsSticky(rect.top <= 0);
    }

    // Update active section
    sections.forEach((section) => {
      const element = document.getElementById(section.id);
      if (element) {
        const rect = element.getBoundingClientRect();

        if (rect.top <= 200 && rect.bottom >= 200) {
          setActiveSection(section.id);
        }
      }
    });
  }, [sections]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.addEventListener("scroll", handleScroll);
      return () => {
        window.removeEventListener("scroll", handleScroll);
      };
    }
  }, [handleScroll]);

  useEffect(() => {
    if (typeof window !== "undefined" && window.innerWidth <= 768) {
      const activeRef = navRefs.current[activeSection];
      if (activeRef) {
        activeRef.scrollIntoView({
          behavior: "smooth",
          inline: "center",
          block: "nearest",
        });
      }
    }
  }, [activeSection]);

  return (
    <nav
      className={`${styles.stickyNav} ${className || ""} ${
        isSticky ? "is-sticky" : ""
      }`}
    >
      <ul className={styles.navList}>
        {sections?.map((section) => (
          <li
            key={section?.id}
            ref={(el) => {
              navRefs.current[section.id] = el;
            }}
            onClick={() => scrollToSection(section?.id)}
            className={
              activeSection === section?.id
                ? `${styles.navItem} ${styles.active}`
                : styles.navItem
            }
          >
            {section?.label}
          </li>
        ))}
      </ul>
    </nav>
  );
};

export default ProgramNav;
